// =====================================================================
// StockProductDialog (Financeiro F2-ext / S59) — cadastra/edita um produto do estoque.
// Criação aceita saldo inicial (+ lote/validade/custo) → vira movimento "entrada".
// Edição NÃO mexe no saldo (isso é via movimento — entrada/saida/ajuste).
// =====================================================================
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Box, Typography, CircularProgress, FormControlLabel, Switch
} from "@material-ui/core";
import { toast } from "react-toastify";
import {
  createStockProduct, updateStockProduct, financeErrorMessage, STOCK_UNITS
} from "../../services/financeService";
import useIsMobile from "../../utils/useIsMobile";

const num = (v) => Number(String(v).replace(",", ".")) || 0;

const StockProductDialog = ({ open, product, unitId, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const editing = !!product;
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [unit, setUnit] = useState("un");
  const [minQty, setMinQty] = useState("");
  const [openingQty, setOpeningQty] = useState("");
  const [lot, setLot] = useState("");
  const [expiry, setExpiry] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [brand, setBrand] = useState("");
  const [location, setLocation] = useState("");
  const [barcode, setBarcode] = useState("");
  const [refrigerated, setRefrigerated] = useState(false);
  const [controlled, setControlled] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(product?.name || "");
      setCategory(product?.category || "");
      setUnit(product?.unit || "un");
      setMinQty(product?.minQty != null ? String(product.minQty) : "");
      setBrand(product?.brand || "");
      setLocation(product?.location || "");
      setBarcode(product?.barcode || "");
      setRefrigerated(!!product?.refrigerated);
      setControlled(!!product?.controlled);
      setOpeningQty(""); setLot(""); setExpiry(""); setUnitCost("");
    }
  }, [open, product]);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Informe o nome do produto."); return; }
    setSaving(true);
    try {
      const attrs = { brand, location, barcode, refrigerated, controlled };
      if (editing) {
        await updateStockProduct(product.id, {
          name, category, unit, minQty: num(minQty), ...attrs
        });
      } else {
        await createStockProduct({
          name, category, unit,
          minQty: num(minQty),
          currentQty: num(openingQty),
          lot: lot || undefined,
          expiry: expiry || undefined,
          unitCost: unitCost !== "" ? num(unitCost) : undefined,
          // [S90] unidade ativa (modelo B); ignorado se a company não tem unidades.
          unitId: unitId != null ? unitId : undefined,
          ...attrs
        });
      }
      toast.success(editing ? "Produto atualizado." : "Produto cadastrado.");
      onSaved && onSaved();
    } catch (err) {
      toast.error(financeErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle>{editing ? "Editar produto" : "Novo produto"}</DialogTitle>
      <DialogContent>
        <TextField label="Nome" value={name} onChange={(e) => setName(e.target.value)} fullWidth margin="dense" autoFocus
          placeholder="ex.: Toxina Botulínica 100U" />
        <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }}>
          <TextField label="Categoria" value={category} onChange={(e) => setCategory(e.target.value)} margin="dense"
            style={{ flexGrow: 1, minWidth: 130 }} placeholder="ex.: Injetável" />
          <TextField select label="Unidade" value={unit} onChange={(e) => setUnit(e.target.value)} margin="dense" style={{ flexGrow: 1, minWidth: 130 }}>
            {STOCK_UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
          </TextField>
        </Box>
        <TextField label="Estoque mínimo (alerta)" type="number" value={minQty} onChange={(e) => setMinQty(e.target.value)}
          fullWidth margin="dense" inputProps={{ step: "0.01", min: "0" }} helperText="Avisa quando o saldo cair até aqui (0 = sem alerta)." />
        <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }}>
          <TextField label="Marca" value={brand} onChange={(e) => setBrand(e.target.value)} margin="dense" style={{ flexGrow: 1, minWidth: 130 }} />
          <TextField label="Local (geladeira/sala)" value={location} onChange={(e) => setLocation(e.target.value)} margin="dense" style={{ flexGrow: 1, minWidth: 130 }} />
        </Box>
        <TextField label="Código de barras (opcional)" value={barcode} onChange={(e) => setBarcode(e.target.value)} fullWidth margin="dense" />
        <Box display="flex" flexWrap="wrap">
          <FormControlLabel control={<Switch checked={refrigerated} onChange={(e) => setRefrigerated(e.target.checked)} color="primary" />} label="❄️ Refrigerado" />
          <FormControlLabel control={<Switch checked={controlled} onChange={(e) => setControlled(e.target.checked)} color="primary" />} label="💊 Controlado (ANVISA)" />
        </Box>

        {!editing && (
          <>
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: "block" }}>
              Saldo inicial (opcional) — vira um movimento de entrada
            </Typography>
            <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }}>
              <TextField label="Qtd. inicial" type="number" value={openingQty} onChange={(e) => setOpeningQty(e.target.value)}
                margin="dense" inputProps={{ step: "0.01", min: "0" }} style={{ flexGrow: 1, minWidth: 130 }} />
              <TextField label="Custo unit. (R$)" type="number" value={unitCost} onChange={(e) => setUnitCost(e.target.value)}
                margin="dense" inputProps={{ step: "0.01", min: "0" }} style={{ flexGrow: 1, minWidth: 130 }} />
            </Box>
            <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }}>
              <TextField label="Lote" value={lot} onChange={(e) => setLot(e.target.value)} margin="dense" style={{ flexGrow: 1, minWidth: 130 }} />
              <TextField label="Validade" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
                margin="dense" InputLabelProps={{ shrink: true }} style={{ flexGrow: 1, minWidth: 150 }} />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockProductDialog;
